import React from 'react';

export type PageHeaderStat = {
  label: string;
  value: React.ReactNode;
  helper?: React.ReactNode;
};

type PageHeaderAccent = 'indigo' | 'amber' | 'emerald' | 'rose' | 'violet' | 'cyan';

export interface PageHeaderProps {
  eyebrow?: string;
  title: string;
  description?: string;
  icon?: React.ReactNode;
  actions?: React.ReactNode;
  stats?: PageHeaderStat[];
  children?: React.ReactNode;
  accent?: PageHeaderAccent;
}

const accentMap: Record<
  PageHeaderAccent,
  { gradient: string; iconBg: string; badge: string; badgeText: string; ring: string }
> = {
  indigo: {
    gradient: 'from-[#EEF2FF]/90 via-white to-[#F8FAFF]',
    iconBg: 'bg-[#E0E7FF] text-[#4338CA] border border-[#4338CA]/20',
    badge: 'border-[#4338CA]/30 bg-[#E0E7FF]/60',
    badgeText: 'text-[#4338CA]',
    ring: 'ring-1 ring-[#6366F1]/15'
  },
  amber: {
    gradient: 'from-[#FFF7ED]/90 via-white to-[#FFFBEB]',
    iconBg: 'bg-[#FEF3C7] text-[#C2410C] border border-[#FBBF24]/30',
    badge: 'border-[#F59E0B]/30 bg-[#FEF3C7]/70',
    badgeText: 'text-[#D97706]',
    ring: 'ring-1 ring-[#FBBF24]/20'
  },
  emerald: {
    gradient: 'from-[#ECFDF5]/90 via-white to-[#F0FDF4]',
    iconBg: 'bg-[#DCFCE7] text-[#15803D] border border-[#34D399]/30',
    badge: 'border-[#10B981]/30 bg-[#DCFCE7]/80',
    badgeText: 'text-[#059669]',
    ring: 'ring-1 ring-[#34D399]/20'
  },
  rose: {
    gradient: 'from-[#FFF1F2]/90 via-white to-[#FDF2F8]',
    iconBg: 'bg-[#FDE2E4] text-[#BE123C] border border-[#FB7185]/30',
    badge: 'border-[#FB7185]/30 bg-[#FEE2E2]/70',
    badgeText: 'text-[#E11D48]',
    ring: 'ring-1 ring-[#FB7185]/20'
  },
  violet: {
    gradient: 'from-[#F5F3FF]/90 via-white to-[#FDF4FF]',
    iconBg: 'bg-[#F3E8FF] text-[#6D28D9] border border-[#C4B5FD]/30',
    badge: 'border-[#8B5CF6]/30 bg-[#EDE9FE]/80',
    badgeText: 'text-[#7C3AED]',
    ring: 'ring-1 ring-[#C084FC]/20'
  },
  cyan: {
    gradient: 'from-[#ECFEFF]/90 via-white to-[#F0FDFA]',
    iconBg: 'bg-[#CFFAFE] text-[#0E7490] border border-[#67E8F9]/30',
    badge: 'border-[#22D3EE]/30 bg-[#CFFAFE]/80',
    badgeText: 'text-[#0E7490]',
    ring: 'ring-1 ring-[#22D3EE]/20'
  }
};

const getStatGridClass = (count: number) => {
  if (count <= 1) return 'sm:grid-cols-1';
  if (count === 2) return 'sm:grid-cols-2 lg:grid-cols-2';
  if (count === 3) return 'sm:grid-cols-2 lg:grid-cols-3';
  return 'sm:grid-cols-2 lg:grid-cols-4';
};

const PageHeader: React.FC<PageHeaderProps> = ({
  eyebrow,
  title,
  description,
  icon,
  actions,
  stats,
  children,
  accent = 'indigo'
}) => {
  const accentTone = accentMap[accent] ?? accentMap.indigo;

  return (
    <section
      className={`relative overflow-hidden rounded-3xl border border-base-300/70 bg-base-100/80 p-6 shadow-soft ring-offset-2 sm:p-8 ${accentTone.ring}`}
    >
      <div className={`absolute inset-0 bg-gradient-to-br ${accentTone.gradient}`} aria-hidden />
      <div className="pointer-events-none absolute -right-16 top-0 h-48 w-48 rounded-full bg-white/40 blur-3xl" />
      <div className="pointer-events-none absolute -left-12 bottom-0 h-44 w-44 rounded-full bg-white/40 blur-3xl" />

      <div className="relative flex flex-wrap items-center justify-between gap-6">
        <div className="flex flex-1 items-start gap-4">
          {icon && (
            <div
              className={`flex h-14 w-14 items-center justify-center rounded-2xl text-2xl shadow-inner ${accentTone.iconBg}`}
            >
              {icon}
            </div>
          )}
          <div className="space-y-2">
            {eyebrow && (
              <span
                className={`inline-flex items-center gap-2 rounded-full px-4 py-1 text-xs font-semibold uppercase tracking-[0.3em] ${accentTone.badge} ${accentTone.badgeText}`}
              >
                {eyebrow}
              </span>
            )}
            <div>
              <h1 className="text-2xl font-semibold text-base-content sm:text-3xl">{title}</h1>
              {description && <p className="mt-2 text-sm text-base-content/70">{description}</p>}
            </div>
          </div>
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>

      {stats && stats.length > 0 && (
        <div className={`relative mt-6 grid gap-4 ${getStatGridClass(stats.length)}`}>
          {stats.map((stat) => (
            <div
              key={stat.label}
              className="rounded-2xl border border-white/50 bg-white/80 px-4 py-3 text-sm shadow-inner backdrop-blur"
            >
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-base-content/50">{stat.label}</p>
              <p className="mt-2 text-xl font-semibold text-base-content">{stat.value}</p>
              {stat.helper && <p className="mt-1 text-xs text-base-content/60">{stat.helper}</p>}
            </div>
          ))}
        </div>
      )}

      {children && <div className="relative mt-6">{children}</div>}
    </section>
  );
};

export default PageHeader;
